import { getDocs, collection, query, orderBy, doc, getDoc, deleteDoc, addDoc, updateDoc, serverTimestamp, where } from "firebase/firestore";
import { db, auth } from '../../../../firebase/config';
import { fetchCompaniesData } from '../../../companies/service/companiesService';

export type CCTVHardware = {
    id: string;
    bankId?: string;
    bankName?: string;
    branchId?: string;
    branchCode?: string;
    branchName?: string;
    cluster?: string;
    region?: string;
    hardwareType?: 'CCTV';
    vendor?: string;
    dvrNvr?: string;
    noOfDvrNvr?: number;
    noOfCamera?: number;
    hdAnalog?: string;
    noOf2MpCamera?: number;
    noOf6MpCamera?: number;
    noOf5MpCamera?: number;
    noOfHdd?: number;
    installationDate?: string;
    visitDate?: string;
    installationStatus?: 'installed' | 'sent' | 'pending';
    location?: string;
    remarks?: string;
    installedBy?: string;
    createdAt?: any;
    updatedAt?: any;
    createdBy?: string;
    companyId?: string;
    [key: string]: any;
};

export type CCTVHardwareFormData = {
    branchId?: string;
    cluster?: string;
    region?: string;
    vendor?: string;
    dvrNvr?: string;
    noOfDvrNvr?: number;
    noOfCamera?: number;
    hdAnalog?: string;
    noOf2MpCamera?: number;
    noOf6MpCamera?: number;
    noOf5MpCamera?: number;
    noOfHdd?: number;
    installationDate?: string;
    visitDate?: string;
    installationStatus?: 'installed' | 'sent' | 'pending';
    location?: string;
    remarks?: string;
};

/**
 * Fetch company ID for the current user (PSO or Bank user)
 */
export async function fetchCompanyId(uid: string, isBankUser: boolean = false): Promise<string | null> {
    try {
        if (isBankUser) {
            // For bank users, check if uid is a bank document ID or if there's a bank with userId == uid
            const directBank = await getDoc(doc(db, "banks", uid));
            if (directBank.exists()) {
                return uid;
            }
            // Try query by userId field
            const banksSnap = await getDocs(collection(db, "banks"));
            for (const bankDoc of banksSnap.docs) {
                const bankData = bankDoc.data();
                if (bankData.userId === uid) {
                    return bankDoc.id;
                }
            }
            return null;
        } else {
            // For PSO users, find which bank has this PSO
            const banksSnap = await getDocs(collection(db, 'banks'));
            for (const bankDoc of banksSnap.docs) {
                const posDocRef = doc(db, 'banks', bankDoc.id, 'pos', uid);
                const posDoc = await getDoc(posDocRef);
                if (posDoc.exists()) {
                    return bankDoc.id;
                }
            }
            return null;
        }
    } catch (error) {
        console.error('Error fetching company ID:', error);
        return null;
    }
}

/**
 * Fetch all CCTV hardware installations for a company
 */
export async function fetchCCTVHardware(companyId: string): Promise<CCTVHardware[]> {
    if (!companyId) {
        console.log('No company ID provided');
        return [];
    }

    try {
        const hardwareSnap = await getDocs(
            query(
                collection(db, "banks", companyId, "cctv_hardware"),
                orderBy("createdAt", "desc")
            )
        );

        const hardwareList = hardwareSnap.docs.map(doc => ({
            ...doc.data(),
            id: doc.id
        })) as CCTVHardware[];

        return hardwareList;
    } catch (error) {
        console.error('Error fetching CCTV hardware:', error);
        throw error;
    }
}

/**
 * Create a new CCTV hardware installation
 */
export async function createCCTVHardware(
    companyId: string,
    hardwareData: Partial<CCTVHardware>
): Promise<string> {
    const uid = auth.currentUser?.uid;
    if (!uid || !companyId) {
        throw new Error('User ID or company ID not found');
    }

    try {
        // Fetch bank and branch details
        const branchDoc = await getDoc(doc(db, 'banks', companyId, 'branch', hardwareData.branchId || ''));
        const branchData = branchDoc.exists() ? branchDoc.data() : {};

        const bankDoc = await getDoc(doc(db, 'banks', companyId));
        const bankData = bankDoc.exists() ? bankDoc.data() : {};

        const cleanData = { ...hardwareData };
        delete cleanData.id;

        const newHardwareData = {
            ...cleanData,
            hardwareType: 'CCTV',
            bankName: bankData.companyName || bankData.name || 'N/A',
            branchCode: branchData.branchCode || 'N/A',
            branchName: branchData.address || 'N/A',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: uid,
            companyId: companyId,
        };

        const docRef = await addDoc(collection(db, 'banks', companyId, 'cctv_hardware'), newHardwareData);
        return docRef.id;
    } catch (error) {
        console.error('Error creating CCTV hardware:', error);
        throw error;
    }
}

/**
 * Update an existing CCTV hardware installation
 */
export async function updateCCTVHardware(
    companyId: string,
    hardwareId: string,
    updateData: Partial<CCTVHardware>
): Promise<void> {
    if (!companyId || !hardwareId) {
        throw new Error('Company ID or hardware ID not provided');
    }

    try {
        const cleanData = { ...updateData };
        delete cleanData.id;

        // If branch changed, update branch details
        if (cleanData.branchId) {
            const branchDoc = await getDoc(doc(db, 'banks', companyId, 'branch', cleanData.branchId));
            const branchData = branchDoc.exists() ? branchDoc.data() : {};
            cleanData.branchCode = branchData.branchCode || cleanData.branchCode;
            cleanData.branchName = branchData.address || cleanData.branchName;
        }

        await updateDoc(doc(db, 'banks', companyId, 'cctv_hardware', hardwareId), {
            ...cleanData,
            updatedAt: serverTimestamp(),
        });
    } catch (error) {
        console.error('Error updating CCTV hardware:', error);
        throw error;
    }
}

/**
 * Delete a CCTV hardware installation
 */
export async function deleteCCTVHardware(companyId: string, hardwareId: string): Promise<void> {
    if (!companyId || !hardwareId) {
        throw new Error('Company ID or hardware ID not provided');
    }

    try {
        await deleteDoc(doc(db, 'banks', companyId, 'cctv_hardware', hardwareId));
    } catch (error) {
        console.error('Error deleting CCTV hardware:', error);
        throw error;
    }
}

/**
 * Fetch branches for a company
 */
export async function fetchBranches(companyId: string): Promise<any[]> {
    try {
        // Fetch locations
        const locSnap = await getDocs(collection(db, "locations"));
        const locList = locSnap.docs.map(d => ({ id: d.id, ...d.data() } as { id: string; name: string }));

        const branchesSnap = await getDocs(collection(db, 'banks', companyId, 'branch'));
        return branchesSnap.docs.map(doc => {
            const branchData = doc.data();
            return {
                id: doc.id,
                ...branchData,
                locationName: locList.find(l => l.id === branchData.locationId)?.name || "N/A"
            };
        });
    } catch (error) {
        console.error('Error fetching branches:', error);
        return [];
    }
}

/**
 * Fetch deployments for a company to get vendor names
 * Uses the same logic as deploymentsService to properly enrich companies
 * @param companyId - The bank/company ID
 * @param uid - User ID (used to find bank context for fetching companies)
 * @param fetchAllDeployments - If true, fetch all deployments regardless of PSO (for vendor display)
 */
export async function fetchDeployments(companyId: string, uid: string, fetchAllDeployments: boolean = false): Promise<Map<string, string>> {
    try {
        // Fetch deployments from banks/{companyId}/companyDeployment
        const deploymentsSnap = await getDocs(collection(db, "banks", companyId, "companyDeployment"));
        const deployments = deploymentsSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        })) as any[];

        // If fetchAllDeployments is true, don't filter by PSO (to show vendors for all branches)
        // Otherwise, filter by PSO ID (same as deploymentsService)
        let filteredDeployments = deployments;
        if (!fetchAllDeployments) {
            filteredDeployments = deployments.filter(deployment => deployment.psoId === uid);
        }

        // Fetch companies using the same method as deploymentsService
        // Use the companyId to determine if we should fetch as bank user
        // For fetching companies, we need to use the correct context
        const bankDoc = await getDoc(doc(db, "banks", companyId));
        const bankData = bankDoc.exists() ? bankDoc.data() : {};
        const isBankUserForCompanies = bankData.userId === uid || companyId === uid;
        const companies = await fetchCompaniesData(uid, isBankUserForCompanies);

        // Filter companies to only include CCTV companies
        const cctvCompanies = companies.filter(company => company.companyType === 'CCTV');

        console.log('CCTV fetchDeployments - Total deployments:', deployments.length);
        console.log('CCTV fetchDeployments - Filtered deployments:', filteredDeployments.length);
        console.log('CCTV fetchDeployments - Total companies:', companies.length);
        console.log('CCTV fetchDeployments - CCTV companies:', cctvCompanies.length);
        console.log('CCTV fetchDeployments - CCTV Companies IDs:', cctvCompanies.map(c => c.id));
        console.log('CCTV fetchDeployments - Deployments:', filteredDeployments.map(d => ({ branchId: d.branchId, companiesId: d.companiesId })));

        // Create a map: branchId -> companyName (vendor name) - only for CCTV companies
        const vendorMap = new Map<string, string>();

        filteredDeployments.forEach(deployment => {
            if (deployment.branchId && deployment.companiesId) {
                // Only match if the company is a CCTV company
                const company = cctvCompanies.find(c => c.id === deployment.companiesId);
                if (company && company.companyName) {
                    // Use the enriched company name (already merged from global if available)
                    vendorMap.set(deployment.branchId, company.companyName);
                    console.log(`CCTV fetchDeployments - Matched branch ${deployment.branchId} to CCTV vendor: ${company.companyName}`);
                } else {
                    console.log(`CCTV fetchDeployments - No CCTV company found for deployment: branchId=${deployment.branchId}, companiesId=${deployment.companiesId}`);
                }
            }
        });

        console.log('CCTV fetchDeployments - Final vendorMap:', Array.from(vendorMap.entries()));
        return vendorMap;
    } catch (error) {
        console.error('Error fetching deployments:', error);
        return new Map();
    }
}

